/**
 * AdminImportArchive.jsx — bulk CSV importer for historical bookings.
 *
 * Writes go into `lbs_bookings_archive` (NOT `lbs_bookings`) with a
 * `legacy_source` tag so operators can filter them apart in the
 * /admin/archive browser. Used mainly for the Booked Scheduler
 * migration (10k+ rows) — but also useful for any post-hoc CSV
 * dump the operator wants to preserve.
 *
 * Ships 2026-07-14 alongside migration 102.
 */
import { useCallback } from "react";
import CsvImporter from "@/components/CsvImporter";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listSitesLite } from "@/lib/api/sites";
import { bulkImportArchive } from "@/lib/api/archive";

const COLUMNS = [
  { key: "reference_number", label: "Reference #",
    description: "Source system's booking reference. Kept as-is for traceability.", example: "BK-2022-08421" },
  { key: "site_code", label: "Site code",
    description: "Optional. lbs_sites.code within the active tenant. Leave blank if unknown.", example: "MEL01" },
  { key: "direction", label: "Direction", required: true,
    description: "inbound or outbound.", example: "inbound" },
  { key: "scheduled_start", label: "Start (ISO 8601)", required: true,
    description: "ISO timestamp with timezone.", example: "2022-08-15T09:00:00+10:00" },
  { key: "scheduled_end", label: "End (ISO 8601)", required: true,
    description: "ISO timestamp with timezone.", example: "2022-08-15T10:00:00+10:00" },
  { key: "actual_start", label: "Actual start",
    description: "Optional. When the truck actually checked in.", example: "2022-08-15T09:02:00+10:00" },
  { key: "actual_end", label: "Actual end",
    description: "Optional. When the truck departed.", example: "2022-08-15T10:15:00+10:00" },
  { key: "status", label: "Status", required: true,
    description: "completed, cancelled, rejected, no_show.", example: "completed" },
  { key: "sender_company", label: "Sender company",
    description: "Free text.", example: "Acme Foods Pty Ltd" },
  { key: "receiver_company", label: "Receiver company",
    description: "Free text.", example: "My Company" },
  { key: "driver_name", label: "Driver name",
    description: "Free text.", example: "Sam Driver" },
  { key: "driver_phone", label: "Driver phone",
    description: "Free text.", example: "0411 222 333" },
  { key: "driver_email", label: "Driver email",
    description: "Free text.", example: "sam@transport.com.au" },
  { key: "vehicle_type", label: "Vehicle type",
    description: "Free text.", example: "B-double" },
  { key: "vehicle_rego", label: "Vehicle rego",
    description: "Free text.", example: "ABC-123" },
  { key: "pallets_in", label: "Pallets in",
    description: "Integer.", example: "26" },
  { key: "pallets_out", label: "Pallets out",
    description: "Integer.", example: "0" },
  { key: "legacy_source", label: "Legacy source",
    description: "Where this row came from. Default 'csv_import'.", example: "booked_scheduler" },
  { key: "legacy_row_id", label: "Legacy row id",
    description: "Source system PK for audit trail. Keep as string.", example: "29874" },
];

const VALID_STATUSES = new Set(["completed", "cancelled", "rejected", "no_show", "approved", "archived"]);
const VALID_DIRECTIONS = new Set(["inbound", "outbound"]);

export default function AdminImportArchive() {
  const { assignments, hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();

  const targetClientId = isSuperAdmin
    ? (viewAsClientId || null)
    : (assignments?.[0]?.client_id || null);

  const loadContext = useCallback(async () => {
    if (!targetClientId) {
      throw new Error(isSuperAdmin
        ? "Super admin: please pick a client in the top bar first."
        : "Your account has no tenant assignment.");
    }
    const { data: sites } = await listSitesLite(targetClientId);
    const sitesByCode = new Map();
    (sites || []).forEach((s) => { if (s.code) sitesByCode.set(s.code.toLowerCase(), s); });
    return { clientId: targetClientId, sitesByCode };
  }, [targetClientId, isSuperAdmin]);

  const validateRow = (raw, ctx) => {
    const errors = [];
    let level = "valid";

    const direction = (raw.direction || "").trim().toLowerCase();
    if (!direction)                       errors.push("direction required");
    else if (!VALID_DIRECTIONS.has(direction)) errors.push("direction must be inbound or outbound");

    const status = (raw.status || "").trim().toLowerCase();
    if (!status)                       errors.push("status required");
    else if (!VALID_STATUSES.has(status)) errors.push(`status must be one of ${[...VALID_STATUSES].join(", ")}`);

    const parseTs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return isNaN(d.getTime()) ? "INVALID" : d.toISOString();
    };
    const scheduledStart = parseTs((raw.scheduled_start || "").trim());
    const scheduledEnd   = parseTs((raw.scheduled_end   || "").trim());
    const actualStart    = parseTs((raw.actual_start    || "").trim());
    const actualEnd      = parseTs((raw.actual_end      || "").trim());
    if (!scheduledStart || scheduledStart === "INVALID") errors.push("scheduled_start invalid");
    if (!scheduledEnd   || scheduledEnd   === "INVALID") errors.push("scheduled_end invalid");
    if (actualStart === "INVALID")                       errors.push("actual_start invalid");
    if (actualEnd   === "INVALID")                       errors.push("actual_end invalid");

    // Optional site match — warn only, doesn't block import.
    const siteCode = (raw.site_code || "").trim();
    const site = siteCode ? ctx.sitesByCode.get(siteCode.toLowerCase()) : null;
    if (siteCode && !site) { errors.push(`site "${siteCode}" not found (kept as legacy metadata)`); level = "warn"; }

    const intOrNull = (s) => {
      if (s === "" || s === null || s === undefined) return null;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? null : n;
    };

    if (errors.length > 0 && level !== "warn") level = "error";

    return {
      level,
      errors,
      normalised: {
        client_id: ctx.clientId,
        site_id: site?.id || null,
        reference_number: (raw.reference_number || "").trim() || null,
        direction,
        status,
        scheduled_start: scheduledStart,
        scheduled_end:   scheduledEnd,
        actual_start:    actualStart || null,
        actual_end:      actualEnd   || null,
        sender_company:   (raw.sender_company   || "").trim() || null,
        receiver_company: (raw.receiver_company || "").trim() || null,
        driver_name:      (raw.driver_name      || "").trim() || null,
        driver_phone:     (raw.driver_phone     || "").trim() || null,
        driver_email:     (raw.driver_email     || "").trim() || null,
        vehicle_type:     (raw.vehicle_type     || "").trim() || null,
        vehicle_rego:     (raw.vehicle_rego     || "").trim() || null,
        pallets_in:  intOrNull(raw.pallets_in),
        pallets_out: intOrNull(raw.pallets_out),
        legacy_source: (raw.legacy_source || "").trim() || undefined,   // batch default in commitBatch
        legacy_row_id: (raw.legacy_row_id || "").trim() || null,
      },
    };
  };

  // The CsvImporter contract commits per-row via `commitRow`. Archive
  // writes are gated behind the service_role backend endpoint, so we
  // route each row through `bulkImportArchive` singleton-batches. Not
  // as efficient as a real batch commit, but works within the existing
  // CsvImporter API and keeps failure reporting per-row (which is what
  // operators need for the Booked migration).
  const commitRow = async (n) => {
    const rows = [n];
    // Prefer the row's own legacy_source; fall back to the CSV-import default.
    const legacySource = n.legacy_source || "csv_import";
    delete n.legacy_source;
    const { data, error } = await bulkImportArchive(rows, { legacySource });
    if (error) return { ok: false, error: error.message };
    if (data?.failed > 0) {
      return { ok: false, error: (data.errors?.[0]?.error) || "insert failed" };
    }
    return { ok: true };
  };

  if (!hasRole("super_admin") && !hasRole("client_admin")) {
    return <p className="text-sm text-rose-700">Admins only.</p>;
  }

  return (
    <div data-testid="admin-import-archive-page">
      {!targetClientId && (
        <div
          className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3"
          data-testid="archive-import-no-tenant"
        >
          {isSuperAdmin
            ? "Pick a client from the top-bar 'View as client' picker before importing."
            : "No tenant assignment found on your account."}
        </div>
      )}
      <CsvImporter
        title="Import archived bookings (historical)"
        testid="archive-import"
        columns={COLUMNS}
        loadContext={loadContext}
        validateRow={validateRow}
        commitRow={commitRow}
      />
      <p className="text-xs text-slate-500 mt-4">
        Rows land in <code>lbs_bookings_archive</code> and are visible on
        the <a href="/admin/archive" className="underline">Archive browser</a>.
        Set <code>legacy_source</code> per-row (e.g. <code>booked_scheduler</code>)
        so you can filter by source later.
      </p>
    </div>
  );
}
